import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Orders = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            When someone purchases tickets, an order is created containing one or
            more ticket items. Each ticket item is assigned to an attendee by first
            name, last name, and email address. Purchasers can use the{' '}
            <Paragraph size='$5' fontWeight='$7'>For me</Paragraph> option to assign
            a ticket to themselves, or enter different attendee details to buy on
            behalf of others.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page showing the ticket purchase flow — orders are created when attendees complete checkout`}
          caption={t`Orders are created when attendees purchase tickets from the Event page`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='order-management' title={t`Order Management`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='clock' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Order statuses</Paragraph> — Orders
                  move through three statuses:{' '}
                  <Paragraph size='$5' fontWeight='$7'>pending</Paragraph> while payment
                  is processing,{' '}
                  <Paragraph size='$5' fontWeight='$7'>succeeded</Paragraph> once payment
                  is confirmed, and{' '}
                  <Paragraph size='$5' fontWeight='$7'>canceled</Paragraph> if the order is
                  voided or refunded. An invoice number is automatically assigned when the
                  order succeeds.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='search' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Viewing orders</Paragraph> —
                  Organizers can view all ticket sales from{' '}
                  <Link href='/docs/events/settings'>Event Settings</Link>. The orders
                  list shows each purchaser, the tiers they selected, the amount paid, any{' '}
                  <Link href='/docs/events/discounts'>discount codes</Link> applied, and
                  the current status. Filter by status to quickly find pending or completed
                  orders.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='money' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Processing refunds</Paragraph> —
                  Organizers can issue refunds for individual orders directly from the
                  order detail view. Refunds are processed through Stripe and returned to
                  the original payment method. The order status updates to canceled and the
                  tickets are released, freeing up availability in the associated{' '}
                  <Link href='/docs/events/tiers'>ticketing tier</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Exporting data</Paragraph> —
                  Export order data for your records, accounting, or reporting needs. The
                  export includes purchaser information, ticket details, amounts, discount
                  codes, and order statuses.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='purchase-flow-link' title={t`Attendee Experience`}>
          <Paragraph size='$5'>
            <Trans>
              For more on the attendee experience when purchasing tickets, see{' '}
              <Link href='/docs/events/purchase-flow'>Attendee Purchase Flow</Link>.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Orders
